import { Bot, User, RotateCcw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { AIMessage, AIAction } from '../../types/ai-assistant-types';

interface AIMessageBubbleProps {
  message: AIMessage;
  onActionClick?: (action: AIAction) => void;
  onRetry?: () => void;
}

export function AIMessageBubble({ message, onActionClick, onRetry }: AIMessageBubbleProps) {
  const isUser = message.role === 'user';
  const isError = !!message.error;

  const timeAgo = (() => {
    try {
      return formatDistanceToNow(new Date(message.timestamp), { addSuffix: true });
    } catch {
      return '';
    }
  })();

  return (
    <div className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser
            ? 'bg-[var(--brand-primary)] text-white'
            : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
        }`}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      <div className={`flex flex-col max-w-[80%] ${isUser ? 'items-end' : 'items-start'}`}>
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
            isUser
              ? 'bg-[var(--brand-primary)] text-white rounded-br-md'
              : isError
                ? 'bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800 rounded-bl-md'
                : 'bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-bl-md'
          }`}
        >
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>

        {message.actions && message.actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {message.actions.map((action, index) => (
              <button
                key={index}
                onClick={() => onActionClick?.(action)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full
                  bg-[var(--brand-primary)] bg-opacity-10 text-[var(--brand-primary)]
                  hover:bg-opacity-20 transition-colors border border-[var(--brand-primary)] border-opacity-20"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {isError && onRetry && (
          <button
            onClick={onRetry}
            className="flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400
              hover:bg-red-50 dark:hover:bg-red-950/30 rounded-full transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            Try again
          </button>
        )}

        <span className={`text-[10px] mt-1 text-[var(--text-tertiary)] ${isUser ? 'text-right' : 'text-left'}`}>
          {timeAgo}
        </span>
      </div>
    </div>
  );
}
